import React, { useState, useEffect } from 'react';
import negocioService from '../services/negocio.service';

// ═══════════════════════════════════════════════════════════════════════════════
// Modal aberto AUTOMATICAMENTE depois que um negócio novo é incluído.
//
// Premissas:
//   - O usuário NÃO pode cancelar (sem botão fechar, sem ESC, sem click no overlay).
//     A data da oportunidade é informação obrigatória pra timeline.
//   - A data não pode ser FUTURA. Default hoje.
//   - A etapa fica fixa em "Oportunidade" (apenas exibida pra contexto).
//
// Backend: POST /api/negocios/:id/definir-data-oportunidade
//   body: { data_evento (YYYY-MM-DD), usuario_id }
// ═══════════════════════════════════════════════════════════════════════════════

const COR = {
  azul: '#003399', azulBg: '#eff6ff',
  branco: '#fff', borda: '#e8eaed',
  texto: '#1a1a2e', muted: '#888',
  vermelho: '#e53935', vermBg: '#fff0f0',
  verde: '#1D9E75',
  oportunidadeCor: '#6b7280',  // cor padrão da etapa Oportunidade
};

const Z_OVERLAY = 10001;
const Z_MODAL   = 10002;

function hojeISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function DefinirDataOportunidadeModal({
  aberto,
  negocio,         // negócio recém-incluído (precisamos só de id e descricao)
  usuarioId,
  onSucesso,       // chamado depois que a data foi salva com sucesso
}) {
  const [dataEvento, setDataEvento] = useState(hojeISO());
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Reseta state ao abrir
  useEffect(() => {
    if (aberto) {
      setDataEvento(hojeISO());
      setErro('');
      setSalvando(false);
    }
  }, [aberto]);

  if (!aberto) return null;

  const handleSalvar = async () => {
    setErro('');
    if (!dataEvento) {
      setErro('Informe a data da oportunidade.');
      return;
    }
    // Valida: data não pode ser futura
    const d = new Date(dataEvento + 'T00:00:00');
    const hoje = new Date(); hoje.setHours(23, 59, 59, 999);
    if (d > hoje) {
      setErro('A data da oportunidade não pode ser futura.');
      return;
    }

    setSalvando(true);
    try {
      await negocioService.definirDataOportunidade(negocio.id, {
        data_evento: dataEvento,
        usuario_id: usuarioId,
      });
      onSucesso && onSucesso(dataEvento);
    } catch (err) {
      console.error('Erro ao definir data da oportunidade:', err);
      setErro(err.response?.data?.mensagem || 'Erro ao definir data.');
    } finally {
      setSalvando(false);
    }
  };

  // Permite Enter pra submeter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !salvando) {
      handleSalvar();
    }
    // ESC bloqueado: NÃO permite cancelar
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <>
      {/* Overlay sem onClick — não fecha ao clicar fora */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: Z_OVERLAY,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '480px',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
        zIndex: Z_MODAL, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '12px',
          backgroundColor: COR.azul, color: COR.branco,
        }}>
          <span style={{ fontSize: '18px' }}>📅</span>
          <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
            Data da Oportunidade
          </span>
        </div>

        {/* Corpo */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Negócio criado (informativo) */}
          <div style={{ fontSize: '12px', color: COR.muted }}>
            Negócio criado: <strong style={{ color: COR.texto }}>{negocio?.id}</strong>
            {negocio?.descricao && <> · {negocio.descricao}</>}
          </div>

          {/* Etapa fixa (informativa) */}
          <div>
            <label style={{
              display: 'block', fontSize: '11px', fontWeight: '600',
              color: COR.muted, marginBottom: '4px',
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>
              Etapa
            </label>
            <div style={{
              padding: '10px 12px', borderRadius: '5px',
              border: `1px solid ${COR.borda}`,
              backgroundColor: '#fafafa',
              display: 'flex', alignItems: 'center', gap: '10px',
              fontSize: '13px', color: COR.texto,
            }}>
              <span style={{
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: COR.oportunidadeCor,
              }} />
              <span style={{ fontWeight: '600' }}>Oportunidade</span>
            </div>
          </div>

          {/* Data */}
          <div>
            <label style={{
              display: 'block', fontSize: '11px', fontWeight: '600',
              color: COR.muted, marginBottom: '4px',
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>
              Data da oportunidade *
            </label>
            <input
              type="date"
              value={dataEvento}
              onChange={(e) => setDataEvento(e.target.value)}
              max={hojeISO()}
              onKeyDown={handleKeyDown}
              autoFocus
              style={{
                width: '100%', padding: '10px 12px',
                fontSize: '13px', borderRadius: '5px',
                border: `1px solid ${COR.borda}`,
                backgroundColor: COR.branco, color: COR.texto,
                outline: 'none', boxSizing: 'border-box',
                fontFamily: "'Manrope', sans-serif",
              }}
            />
            <div style={{ fontSize: '11px', color: COR.muted, marginTop: '4px' }}>
              Data em que a oportunidade efetivamente surgiu. Não pode ser futura.
            </div>
          </div>

          {/* Erro */}
          {erro && (
            <div style={{
              padding: '8px 12px', borderRadius: '5px',
              backgroundColor: COR.vermBg, color: COR.vermelho,
              fontSize: '12px',
              border: `1px solid ${COR.vermelho}33`,
            }}>{erro}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button
            onClick={handleSalvar}
            disabled={salvando}
            style={{
              padding: '8px 20px', fontSize: '13px', fontWeight: '600',
              border: 'none', borderRadius: '5px',
              backgroundColor: salvando ? COR.muted : COR.azul,
              color: COR.branco,
              cursor: salvando ? 'default' : 'pointer',
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            {salvando ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </>
  );
}

export default DefinirDataOportunidadeModal;
